import { onBeforeUnmount, watch } from "vue";
import { useRoute } from "vue-router";
import { appEvents } from "@/events/appEvents";
import { isFundMode, fundModeExplainStepId } from "@/help/fundModeFeedback";
import { useInteractiveHelpStore } from "@/store/interactiveHelpStore";
const FUNDS_GUIDE_ID = "funds.paradigm-intro";
const SALES_MANAGE_GUIDE_ID = "sales.manage-intro";
const SALES_BUY_GUIDE_ID = "sales.buy-intro";
const DONATIONS_COMMIT_GUIDE_ID = "donations.commit-intro";
const PAYMENTS_REGISTER_GUIDE_ID = "payments.register-intro";
const MODES_STEP_ID = "explain-modes";
const syncStepToRoute = (guideId, routeName, helpStore) => {
    const guide = helpStore.activeGuide;
    if (!guide || helpStore.activeGuideId !== guideId)
        return;
    const applicable = guide.steps.filter((step) => !step.when || step.when(helpStore.context));
    const currentIdx = applicable.findIndex((step) => step.id === helpStore.activeStepId);
    if (currentIdx < 0)
        return;
    const currentStep = applicable[currentIdx];
    if (currentStep.routeName === routeName)
        return;
    const onRoute = applicable
        .map((step, index) => ({ step, index }))
        .filter(({ step }) => step.routeName === routeName);
    if (!onRoute.length)
        return;
    const before = onRoute.filter(({ index }) => index < currentIdx);
    const target = before.length ? before[before.length - 1] : onRoute[0];
    helpStore.goToStep(target.step.id);
};
export const useGuideEngine = () => {
    const helpStore = useInteractiveHelpStore();
    const route = useRoute();
    let subscribedEvent = null;
    const onAdvanceEvent = () => {
        if (!subscribedEvent)
            return;
        helpStore.handleAdvanceEvent(subscribedEvent);
    };
    const onLeftSameAmountPath = () => {
        if (!helpStore.isActive)
            return;
        if (helpStore.activeGuideId !== FUNDS_GUIDE_ID)
            return;
        helpStore.cancelGuide();
        helpStore.reset();
    };
    const onModeSelected = (mode) => {
        if (!helpStore.isActive)
            return;
        if (helpStore.activeGuideId !== FUNDS_GUIDE_ID)
            return;
        if (!isFundMode(mode))
            return;
        helpStore.setContext({ fundMode: mode });
        helpStore.goToStep(fundModeExplainStepId(mode));
    };
    const onModeChosen = (mode) => {
        if (!helpStore.isActive)
            return;
        if (helpStore.activeGuideId !== FUNDS_GUIDE_ID)
            return;
        if (!isFundMode(mode))
            return;
        helpStore.setContext({ fundMode: mode });
        const stepId = helpStore.activeStepId;
        if (stepId === MODES_STEP_ID || stepId?.startsWith("explain-mode-")) {
            helpStore.nextStep();
        }
    };
    const onReturnedToModes = () => {
        if (!helpStore.isActive)
            return;
        if (helpStore.activeGuideId !== FUNDS_GUIDE_ID)
            return;
        helpStore.setContext({ fundMode: undefined });
        helpStore.goToStep(MODES_STEP_ID);
    };
    /** Cierra la guía aunque el usuario cree el fondo sin pulsar «Siguiente» en el resumen. */
    const onWizardCreated = () => {
        if (!helpStore.isActive)
            return;
        if (helpStore.activeGuideId !== FUNDS_GUIDE_ID)
            return;
        helpStore.completeGuide();
        helpStore.reset();
    };
    const onSaleOrderConfirmed = () => {
        if (!helpStore.isActive)
            return;
        if (helpStore.activeGuideId !== SALES_BUY_GUIDE_ID)
            return;
        helpStore.completeGuide();
        helpStore.reset();
    };
    const onDonationCommitmentConfirmed = () => {
        if (!helpStore.isActive)
            return;
        if (helpStore.activeGuideId !== DONATIONS_COMMIT_GUIDE_ID)
            return;
        helpStore.completeGuide();
        helpStore.reset();
    };
    const unsubscribeCurrent = () => {
        if (!subscribedEvent)
            return;
        appEvents.off(subscribedEvent, onAdvanceEvent);
        subscribedEvent = null;
    };
    appEvents.on("fund:wizard_left_same_amount_path", onLeftSameAmountPath);
    appEvents.on("fund:wizard_created", onWizardCreated);
    appEvents.on("fund:wizard_mode_chosen", onModeChosen);
    appEvents.on("fund:wizard_mode_selected", onModeSelected);
    appEvents.on("fund:wizard_returned_to_modes", onReturnedToModes);
    appEvents.on("sale:order_confirmed", onSaleOrderConfirmed);
    appEvents.on("donation:commitment_confirmed", onDonationCommitmentConfirmed);
    watch(() => ({
        step: helpStore.activeStep,
        active: helpStore.isActive,
    }), ({ step, active }) => {
        unsubscribeCurrent();
        const eventName = step?.advanceOn?.event;
        if (!active || !eventName)
            return;
        if (eventName === "fund:wizard_created" ||
            eventName === "fund:wizard_mode_chosen" ||
            eventName === "fund:wizard_mode_selected" ||
            eventName === "sale:order_confirmed" ||
            eventName === "donation:commitment_confirmed") {
            return;
        }
        subscribedEvent = eventName;
        appEvents.on(eventName, onAdvanceEvent);
    }, { immediate: true });
    watch(() => route.name, (routeName) => {
        if (!helpStore.isActive || !routeName)
            return;
        if (helpStore.activeGuideId === SALES_BUY_GUIDE_ID) {
            syncStepToRoute(SALES_BUY_GUIDE_ID, String(routeName), helpStore);
        }
        else if (helpStore.activeGuideId === SALES_MANAGE_GUIDE_ID) {
            syncStepToRoute(SALES_MANAGE_GUIDE_ID, String(routeName), helpStore);
        }
        else if (helpStore.activeGuideId === PAYMENTS_REGISTER_GUIDE_ID) {
            syncStepToRoute(PAYMENTS_REGISTER_GUIDE_ID, String(routeName), helpStore);
        }
    });
    onBeforeUnmount(() => {
        unsubscribeCurrent();
        appEvents.off("fund:wizard_left_same_amount_path", onLeftSameAmountPath);
        appEvents.off("fund:wizard_created", onWizardCreated);
        appEvents.off("fund:wizard_mode_chosen", onModeChosen);
        appEvents.off("fund:wizard_mode_selected", onModeSelected);
        appEvents.off("fund:wizard_returned_to_modes", onReturnedToModes);
        appEvents.off("sale:order_confirmed", onSaleOrderConfirmed);
        appEvents.off("donation:commitment_confirmed", onDonationCommitmentConfirmed);
    });
};
